import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabPanel } from '../ui';
import { getCompanySettings, updateCompanySettings } from '../../services/syncApi';
import ShipToFreightSettings from './ShipToFreightSettings';
import Warehouses from '../inventory/Warehouses';

// Ops-editable configuration, split into tabs:
//   - Company: per-company (AU/NZ) fuel levy (FUELLEVY company extra field on the B2B company push).
//   - Ship To Addresses: freight delivery zones (Type/FSTD/FEXP address extra fields).
//   - Warehouses: Epicor warehouse master with stock aggregates and the in-use toggle.

const COMPANIES = ['AU', 'NZ'];

const TABS = [
  { id: 'company', label: 'Company' },
  { id: 'shipto', label: 'Ship To Addresses' },
  { id: 'warehouses', label: 'Warehouses' },
];

const errToText = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (raw.message) return raw.message;
  if (Array.isArray(raw)) return raw.map((r) => r.msg || JSON.stringify(r)).join('; ');
  try { return JSON.stringify(raw); } catch { return String(raw); }
};
const formatErr = (e) => errToText(e?.response?.data?.detail || e?.message || 'request failed');

const CompanyTab = () => {
  const [rows, setRows] = useState({});
  const [drafts, setDrafts] = useState({});
  const [error, setError] = useState(null);
  const [savingCompany, setSavingCompany] = useState(null);
  const [savedCompany, setSavedCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCompanySettings();
      const byCompany = {};
      const draftByCompany = {};
      data.forEach((r) => {
        byCompany[r.company] = r;
        draftByCompany[r.company] = String(r.fuel_levy);
      });
      setRows(byCompany);
      setDrafts(draftByCompany);
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (company) => {
    setError(null);
    setSavedCompany(null);
    const value = drafts[company];
    const parsed = Number(value);
    if (value === '' || Number.isNaN(parsed) || parsed < 0) {
      setError(`Fuel levy for ${company} must be a number ≥ 0`);
      return;
    }
    setSavingCompany(company);
    try {
      const updated = await updateCompanySettings(company, { fuel_levy: parsed });
      setRows((prev) => ({ ...prev, [company]: updated }));
      setDrafts((prev) => ({ ...prev, [company]: String(updated.fuel_levy) }));
      setSavedCompany(company);
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setSavingCompany(null);
    }
  };

  return (
    <>
      <div className="value-lookups-header" style={{ marginTop: '1rem' }}>
        <div />
        <button type="button" className="ghost" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      <p className="muted">
        Per-company values pushed to BigCommerce B2B companies. Fuel levy maps to the{' '}
        <code>FUELLEVY</code> company extra field. A change here is applied to existing
        companies on the next Company <strong>Full</strong> Sync (incremental syncs only
        touch companies whose Epicor record changed).
      </p>

      {error && (
        <div className="card error-banner">
          <strong>Error:</strong> {error}
          <button type="button" className="ghost" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <section className="card">
        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <table className="value-lookups-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Fuel levy</th>
                <th>Last updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {COMPANIES.map((company) => {
                const row = rows[company];
                const dirty = row && String(row.fuel_levy) !== String(drafts[company]);
                return (
                  <tr key={company}>
                    <td><strong>{company}</strong></td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={drafts[company] ?? ''}
                        onChange={(e) =>
                          setDrafts((prev) => ({ ...prev, [company]: e.target.value }))
                        }
                        disabled={!row}
                        style={{ width: '8rem' }}
                      />
                    </td>
                    <td className="muted">
                      {row?.updated_at
                        ? `${new Date(row.updated_at).toLocaleString()}${row.updated_by ? ` · ${row.updated_by}` : ''}`
                        : '—'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="primary"
                        onClick={() => save(company)}
                        disabled={!row || !dirty || savingCompany === company}
                      >
                        {savingCompany === company ? 'Saving…' : 'Save'}
                      </button>
                      {savedCompany === company && !dirty && (
                        <span className="muted" style={{ marginLeft: '0.5rem' }}>Saved ✓</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {COMPANIES.every((c) => !rows[c]) && !loading && (
                <tr><td colSpan={4} className="muted">No company settings found.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
};

const CompanySettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const tab = TABS.some((t) => t.id === requested) ? requested : 'company';
  const setTab = (next) => {
    setSearchParams(next === 'company' ? {} : { tab: next }, { replace: true });
  };
  return (
    <div className="company-settings">
      <header className="value-lookups-header">
        <h1>Configuration</h1>
      </header>
      <Tabs tabs={TABS} active={tab} onChange={setTab} label="Configuration sections" />
      <TabPanel id="company" active={tab}>
        <CompanyTab />
      </TabPanel>
      <TabPanel id="shipto" active={tab}>
        <ShipToFreightSettings />
      </TabPanel>
      <TabPanel id="warehouses" active={tab}>
        <Warehouses embedded />
      </TabPanel>
    </div>
  );
};

export default CompanySettings;
